import React from 'react';
import { useState } from 'react';
// Beskrivande text
// Iconer

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrash,
  faPlus,
  faEdit,
  faCheck,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';

import type { StockKeepingUnit } from 'domain-model/types/oms';

interface StockKeepingUnitsInputProps {
  name: string;
  disabled?: boolean;
  value: StockKeepingUnit[];
  onChange: (
    key: string,
    value: StockKeepingUnit[],
    modifiedChildEntity?: any,
  ) => void;
}

export default function StockKeepingUnitsInput(
  props: StockKeepingUnitsInputProps,
) {
  const { name, disabled, value, onChange } = props;
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [newUnit, setNewUnit] = useState<Omit<StockKeepingUnit, 'metadata'>>({
    name: '',
    isMainUnit: false,
    grossWeight: 0,
    minOrderQuantity: 1,
    stepOrderIncrement: 1,
    quantityOfMainUnitPerUnit: 1,
  });

  const handleChange = async (
    newValue: StockKeepingUnit[],
    modifiedChildEntity?: any,
  ) => {
    if (newValue === null || newValue === undefined) {
      newValue = [];
    }
    await onChange(name, newValue, modifiedChildEntity);
  };

  const handleAdd = () => {
    if (!newUnit.name.trim()) return;
    const unitToAdd: StockKeepingUnit = {
      ...newUnit,
      metadata: {}, // Initialize empty metadata
    };

    if (value === null || value === undefined) {
      handleChange([unitToAdd]);
    } else {
      handleChange([...value, unitToAdd]);
    }

    setNewUnit({
      name: '',
      isMainUnit: false,
      grossWeight: 0,
      minOrderQuantity: 1,
      stepOrderIncrement: 1,
      quantityOfMainUnitPerUnit: 1,
    });
    setIsAdding(false);
  };

  const handleEdit = (
    unit: StockKeepingUnit,
    index: number,
    field: keyof Omit<StockKeepingUnit, 'metadata'>,
    newValue: any,
  ) => {
    const newUnits = { ...unit, [field]: newValue };
    handleChange([
      ...(value ?? []).slice(0, index),
      newUnits,
      ...(value ?? []).slice(index + 1),
    ]);
  };

  const handleRemove = (index: number) => {
    const newUnits = value.filter((_, i) => i !== index);
    handleChange(newUnits);
  };

  const startEditing = (index: number) => {
    setEditingIndex(index);
    setIsAdding(false);
  };

  const stopEditing = () => {
    setEditingIndex(null);
  };

  const renderUnitForm = (
    unit: StockKeepingUnit | Omit<StockKeepingUnit, 'metadata'>,
    onUpdate: (
      field: keyof Omit<StockKeepingUnit, 'metadata'>,
      value: any,
    ) => void,
    isNew = false,
  ) => (
    <div className="row g-3">
      <div className="col-md-6">
        <label htmlFor={`${name}-name`} className="form-label">
          Enhet *
        </label>
        <input
          id={`${name}-name`}
          className="form-control"
          value={unit.name}
          onChange={e => onUpdate('name', e.target.value)}
          disabled={disabled}
          placeholder="Enter SKU name"
        />
      </div>

      <div className="col-md-6">
        <label htmlFor={`${name}-grossWeight`} className="form-label">
          Bruttovikt
        </label>
        <input
          id={`${name}-grossWeight`}
          className="form-control"
          type="number"
          step="0.01"
          value={unit.grossWeight}
          onChange={e =>
            onUpdate('grossWeight', Number.parseFloat(e.target.value))
          }
          disabled={disabled}
        />
      </div>

      <div className="col-md-6">
        <label htmlFor={`${name}-minOrderQuantity`} className="form-label">
          Minsta kvantitet
        </label>
        <input
          id={`${name}-minOrderQuantity`}
          className="form-control"
          type="number"
          min="1"
          value={unit.minOrderQuantity}
          onChange={e =>
            onUpdate('minOrderQuantity', Number.parseInt(e.target.value))
          }
          disabled={disabled}
        />
      </div>

      <div className="col-md-6">
        <label htmlFor={`${name}-stepOrderIncrement`} className="form-label">
          Kvantitetssteg
        </label>
        <input
          id={`${name}-stepOrderIncrement`}
          className="form-control"
          type="number"
          min="1"
          value={unit.stepOrderIncrement}
          onChange={e =>
            onUpdate('stepOrderIncrement', Number.parseInt(e.target.value))
          }
          disabled={disabled}
        />
      </div>

      <div className="col-md-6">
        <label
          htmlFor={`${name}-quantityOfMainUnitPerUnit`}
          className="form-label"
        >
          Huvudenheter per enhet
        </label>
        <input
          id={`${name}-quantityOfMainUnitPerUnit`}
          className="form-control"
          type="number"
          step="0.01"
          min="0.01"
          value={unit.quantityOfMainUnitPerUnit}
          onChange={e =>
            onUpdate(
              'quantityOfMainUnitPerUnit',
              Number.parseFloat(e.target.value),
            )
          }
          disabled={disabled}
        />
      </div>

      <div className="col-md-6 d-flex align-items-center">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id={`${name}-isMainUnit`}
            checked={unit.isMainUnit}
            onChange={e => onUpdate('isMainUnit', e.target.checked)}
            disabled={disabled}
          />
          <label className="form-check-label" htmlFor={`${name}-isMainUnit`}>
            Är huvudenhet
            {/* Is Main Unit --- IGNORE --- */}
          </label>
        </div>
      </div>
    </div>
  );

  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <label className="form-label fw-medium fs-5 mb-0">
          Förpackningasenheter
        </label>
        {!disabled && (
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={() => setIsAdding(true)}
            disabled={isAdding}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" size="sm" />
            Lägg till förpackningsenhet
            {/* Add SKU --- IGNORE --- */}
          </button>
        )}
      </div>

      {/* Add new unit form */}
      {isAdding && (
        <div className="card mb-3">
          <div className="card-header">
            <h5 className="card-title mb-0">Lägg till ny förpackningsenhet</h5>
          </div>
          <div className="card-body">
            {renderUnitForm(
              newUnit,
              (field, value) =>
                setNewUnit(prev => ({ ...prev, [field]: value })),
              true,
            )}
            <div className="d-flex gap-2 mt-3">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAdd}
                disabled={!newUnit.name.trim()}
              >
                <FontAwesomeIcon icon={faCheck} className="me-2" size="sm" />
                Lägg till
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setIsAdding(false)}
              >
                <FontAwesomeIcon icon={faTimes} className="me-2" size="sm" />
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Existing units */}
      {value === null ||
        (value.length === 0 && !isAdding && (
          <div className="text-center py-5 text-muted">
            Inga artikelnummer är definierade. Klicka på Lägg till SKU för att
            komma igång.
          </div>
        ))}

      {value !== undefined &&
        value !== null &&
        value.length > 0 &&
        value.map((unit, index) => (
          <div key={index} className="card mb-3">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <h5 className="card-title mb-0">{unit.name}</h5>
                  {unit.isMainUnit && (
                    <span className="badge bg-primary">Huvudenhet</span>
                  )}
                </div>
                {!disabled && (
                  <div className="d-flex gap-2">
                    {editingIndex === index ? (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={stopEditing}
                      >
                        <FontAwesomeIcon icon={faTimes} size="sm" />
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => startEditing(index)}
                        >
                          <FontAwesomeIcon icon={faEdit} size="sm" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => handleRemove(index)}
                        >
                          <FontAwesomeIcon icon={faTrash} size="sm" />
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="card-body">
              {editingIndex === index ? (
                <div>
                  {renderUnitForm(unit, (field, value) =>
                    handleEdit(unit, index, field, value),
                  )}
                </div>
              ) : (
                <div className="row g-3">
                  <div className="col-md-6">
                    <strong>Bruttovikt:</strong> {unit.grossWeight}
                  </div>
                  <div className="col-md-6">
                    <strong>Minsta kvantitet:</strong> {unit.minOrderQuantity}
                  </div>
                  <div className="col-md-6">
                    <strong>Kvantitetssteg:</strong> {unit.stepOrderIncrement}
                  </div>
                  <div className="col-md-6">
                    <strong>Huvudenheter per enhet:</strong>{' '}
                    {unit.quantityOfMainUnitPerUnit}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
    </div>
  );
}
